import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select, type SelectOptionGroup } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '@wundo/shared/language.ts';
import { AUTOMATIC_LANGUAGE, languageOptions } from '@/utils/language.ts';

const supported = new Set<string>(SUPPORTED_LANGUAGES);

/*
 * Two named groups rather than one list with a rule of dashes in the middle.
 * The four we ship features for are what almost every record is set to, and
 * the other hundred-and-eighty are there so the rare one is possible at all —
 * `<optgroup>` is how the platform already says that, and every screen reader
 * already reads out which group the highlighted option is in.
 */
const LANGUAGE_GROUPS: readonly SelectOptionGroup[] = [
  {
    label: 'Supported',
    options: languageOptions.filter((option) => supported.has(option.value)),
  },
  {
    label: 'All languages',
    options: languageOptions.filter(
      (option) => option.value !== '---' && !supported.has(option.value),
    ),
  },
];

const AUTOMATIC_OPTION = { value: AUTOMATIC_LANGUAGE, label: 'Automatic' };

export interface LanguageSelectProps {
  value: string;
  onChange: (value: string) => void;
  /** Put on the `<select>`, so a `<label htmlFor>` names it. */
  id?: string;
  disabled?: boolean;
  required?: boolean;
  /** Offer "Automatic": the first contact (Teams or the browser) picks the language. */
  allowAutomatic?: boolean;
  'aria-label'?: string;
  className?: string;
}

/**
 * Which language Wundo speaks to somebody in.
 *
 * Handed an empty value it settles on the signed-in staff member's own
 * language rather than sitting on a blank: a new record is nearly always for
 * a colleague of whoever is filling the form in, and a select with nothing in
 * it reads as a question that has not been asked yet.
 */
export function LanguageSelect({
  value,
  onChange,
  id,
  disabled = false,
  required = false,
  allowAutomatic = false,
  className,
  'aria-label': ariaLabel,
}: LanguageSelectProps) {
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  useEffect(() => {
    if (!value && currentUser) {
      onChange(currentUser.language || DEFAULT_LANGUAGE);
    }
  }, [value, currentUser, onChange]);

  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      options={allowAutomatic ? [AUTOMATIC_OPTION, ...LANGUAGE_GROUPS] : LANGUAGE_GROUPS}
      value={value}
      required={required}
      disabled={disabled}
      className={className}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
